/**
 * Feature-owned save sections (FINISH_PLAN WP0): each feature that keeps state outside the profile (a wallet, the
 * market, events…) owns one named section of the save instead of adding fields and validate lines to saves.ts. A
 * section is stored as its own JSON under `SaveRecord.sections[name]`; the feature's `parse` validates it on every read
 * and its `serialize` writes it. A section nobody registers is kept untouched, so a save written by a build with more
 * features survives a trip through one with fewer.
 */

/** The section names the finish plan agreed (PLAN.md §4 WP0.6). A new name is added here, never reused for new content. */
export const SAVE_SECTION_NAMES = ["wallet", "market", "shop", "tribute", "events", "trials", "achievements", "stats", "cosmetics", "deals", "vip", "shops", "modes", "research", "cards", "season", "dailyRoad", "eventModes", "consumables", "offerTriggers", "records", "trophies", "pets", "boosts"] as const;
export type SaveSectionName = (typeof SAVE_SECTION_NAMES)[number];

/** What a section handle reads and writes: the save record's section map (SaveRecord has it). */
export interface HasSections { sections: Record<string, unknown> }

/** A registered section: typed access to one entry of a record's section map. */
export interface SaveSection<T> {
  readonly name: SaveSectionName;
  /** The section's value in `record`, parsed and validated; undefined when absent or unreadable (the raw JSON stays). */
  read(record: HasSections): T | undefined;
  /** Serialises `value` into `record`; the caller stores the record as usual (SaveService.save). */
  write(record: HasSections, value: T): void;
  /** Removes the section from `record`. */
  clear(record: HasSections): void;
  /**
   * Ties the section to live state: every SaveService.save of `record` first writes `current()` into it, so a feature
   * never has to remember to write before the game saves. Returns the unbind.
   */
  bind(record: HasSections, current: () => T): () => void;
}

interface Entry { readonly serialize: (value: never) => unknown; readonly bound: Map<HasSections, () => unknown> }

/** The section registry. The game uses the shared `saveSections`; tests make their own. */
export class SaveSections {
  private readonly entries = new Map<SaveSectionName, Entry>();

  /** Claims `name`. Two features claiming one section is a wiring bug, so it throws. */
  register<T>(name: SaveSectionName, parse: (json: unknown) => T | undefined, serialize: (value: T) => unknown): SaveSection<T> {
    if (!(SAVE_SECTION_NAMES as readonly string[]).includes(name)) throw new Error(`unknown save section "${name}"`);
    if (this.entries.has(name)) throw new Error(`save section "${name}" is already registered`);
    const bound = new Map<HasSections, () => unknown>();
    this.entries.set(name, { serialize: serialize as (value: never) => unknown, bound });
    const write = (record: HasSections, value: T) => { record.sections[name] = serialize(value); };
    return {
      name,
      read: (record) => {
        const raw = record.sections[name];
        if (raw === undefined) return undefined;
        try { return parse(raw); } catch { return undefined; }
      },
      write,
      clear: (record) => { delete record.sections[name]; },
      bind: (record, current) => {
        bound.set(record, current);
        return () => { if (bound.get(record) === current) bound.delete(record); };
      },
    };
  }

  /** Whether `name` is claimed (a test and debug aid). */
  has(name: SaveSectionName): boolean { return this.entries.has(name); }

  /** Writes every bound section's live value into `record` (SaveService.save, just before it stores). */
  flush(record: HasSections): void {
    for (const [name, e] of this.entries) {
      const current = e.bound.get(record);
      if (current) record.sections[name] = (e.serialize as (value: unknown) => unknown)(current());
    }
  }
}

/** The game's registry: features register their sections at module load, before the save is read. */
export const saveSections = new SaveSections();

/** Registers a feature's save section on the game's registry (see SaveSections.register). */
export function registerSaveSection<T>(name: SaveSectionName, parse: (json: unknown) => T | undefined, serialize: (value: T) => unknown): SaveSection<T> {
  return saveSections.register(name, parse, serialize);
}

/** The section map of a loaded document: a plain object of JSON values, else empty (an old or damaged save). */
export function parseSections(v: unknown): Record<string, unknown> {
  if (typeof v !== "object" || v === null || Array.isArray(v)) return {};
  return { ...(v as Record<string, unknown>) };
}
